import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import TextField from './TextField';

const NestedSelect = ({
  toggleComponent,
  options,
  error,
  touched,
  searchable,
  scrollable,
  shrink,
  drop,
  align,
  name,
  displayName,
  handleChange,
  toggleClasses,
  fullWidth,
  required,
  disableIconGrayscale,
  disabled,
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [nestedShow, setNestedShow] = useState(
    Array.from(Array(options.length).keys()).fill(false),
  );

  const shrinkLabel = shrink ? ' shrink-label' : '';

  const toggleNested = (index) => {
    const tempNested = [...nestedShow].map((s, i) => (i === index ? !s : false));
    setNestedShow(tempNested);
  };

  const onRootClose = (meta) => {
    if (meta.source === 'rootClose') {
      const tempNested = [...nestedShow].fill(false);
      setNestedShow(tempNested);
    }
  };

  return (
    <div
      className={`multi-select ${disableIconGrayscale ? 'disable-icon-grayscale' : ''} ${
        fullWidth ? 'w-100' : ''
      } ${disabled ? 'pe-none opacity-50' : ''}`}
    >
      {displayName && (
        <label className={`mb-2${shrinkLabel}`}>
          {t(displayName)}
          {required && <span className='lh-1 theme-size-1_5 theme-text-red-1 px-1'>*</span>}
        </label>
      )}

      <Dropdown drop={drop ?? 'down'} autoClose={true} onToggle={(_, meta) => onRootClose(meta)}>
        <Dropdown.Toggle
          id='dropdown-autoclose-true'
          variant='none'
          className={`rounded-sm cursor-pointer ${toggleClasses ?? ''}`}
          as='div'
        >
          {toggleComponent}
          {/* {label && <label className='theme-size-0_9 cursor-pointer'>{t(label)}</label>}
          <img src={chevronGreyIcon} className={`rotate-180`} /> */}
        </Dropdown.Toggle>
        {!touched && error && <p className='form-field-error'>{t('error ' + error)}</p>}

        <Dropdown.Menu className={`${fullWidth ? 'w-100' : ''}`} align={align ?? 'start'} as='div'>
          {searchable && (
            <div className='px-2'>
              <TextField
                value={searchTerm}
                name='search'
                handleChange={(name, value) => setSearchTerm(value)}
                placeholder='field_search'
                classes='w-100 px-1 my-2'
                shrink
              />
            </div>
          )}

          <div className={`px-2 ${scrollable ? 'scrollable-options' : ''}`}>
            {(() => {
              const filteredOptions =
                searchable && searchTerm.length
                  ? options.filter((option) =>
                      option.displayValue.toLowerCase().includes(searchTerm.toLowerCase()),
                    )
                  : options;

              if (filteredOptions.length === 0) {
                return (
                  <Dropdown.Item as='div' disabled className='d-block text-center px-1 text-muted'>
                    {t('text_empty_list')}
                  </Dropdown.Item>
                );
              }

              return filteredOptions.map((option, i) => (
                <Fragment key={i}>
                  {option.nestedOptions ? (
                    <Dropdown.Item
                      as='div'
                      className='px-1 cursor-pointer'
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleNested(i);
                      }}
                      disabled={option.disabled ?? false}
                    >
                      {option.icon && <img src={option.icon} />}
                      {t(option.displayValue)}
                      {option.nestedOptions.length > 0 && (
                        <NestedMenu
                          options={option.nestedOptions}
                          name={option.value}
                          handleChange={handleChange}
                          show={nestedShow[i]}
                        />
                      )}
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item
                      as='div'
                      className='px-1 cursor-pointer '
                      onClick={() => handleChange(name, option.value)}
                      disabled={option.disabled ?? false}
                    >
                      {option.icon && <img src={option.icon} />}
                      {t(option.displayValue)}
                    </Dropdown.Item>
                  )}
                </Fragment>
              ));
            })()}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

NestedSelect.propTypes = {
  toggleComponent: PropTypes.object.isRequired,
  toggleClasses: PropTypes.string,
  options: PropTypes.array.isRequired,
  error: PropTypes.string,
  touched: PropTypes.bool,
  searchable: PropTypes.bool,
  scrollable: PropTypes.bool,
  shrink: PropTypes.bool,
  drop: PropTypes.string,
  align: PropTypes.string,
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  disableIconGrayscale: PropTypes.bool,
};

const NestedMenu = ({ options, show, drop, align, name, handleChange }) => {
  const { t } = useTranslation();

  return (
    <Dropdown drop={drop ?? 'down'} show>
      <Dropdown.Toggle id='dropdown-autoclose-false' variant='none' className='p-0' as='div'>
        <img src={chevronGreyIcon} className='rotate-90 no-effect' />
      </Dropdown.Toggle>

      {show && (
        <Dropdown.Menu className='px-2' align={align ?? 'start'} as='div'>
          {options.map((option, i) => (
            <Dropdown.Item
              key={i}
              as='div'
              className='px-1 cursor-pointer'
              onClick={() => handleChange(name, option.value)}
              disabled={option.disabled ?? false}
            >
              {option.icon && <img src={option.icon} />}
              {t(option.displayValue)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

NestedMenu.propTypes = {
  options: PropTypes.array.isRequired,
  show: PropTypes.bool.isRequired,
  drop: PropTypes.string,
  align: PropTypes.string,
  name: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default NestedSelect;
